import React from 'react';
import { MdExpandMore } from 'react-icons/md';

// Closes the panel when the user clicks anywhere outside `ref`.
function useOutsideAlerter(ref, setOpen) {
  React.useEffect(() => {
    function handleClickOutside(event) {
      if (ref.current && !ref.current.contains(event.target)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [ref, setOpen]);
}

// A checkbox dropdown that looks like the form's native <select>s but allows
// picking multiple values. `options` is [{ value, label }]; `selected` is an
// array of `value`s; `onChange` receives the full new array on every toggle.
const MultiSelectDropdown = ({
  options = [],
  selected = [],
  onChange,
  placeholder = 'Select options',
  disabled = false,
}) => {
  const wrapperRef = React.useRef(null);
  const [open, setOpen] = React.useState(false);
  useOutsideAlerter(wrapperRef, setOpen);

  const toggle = (value) => {
    onChange(
      selected.includes(value)
        ? selected.filter((v) => v !== value)
        : [...selected, value]
    );
  };

  const label = selected.length
    ? options
        .filter((o) => selected.includes(o.value))
        .map((o) => o.label)
        .join(', ')
    : placeholder;

  return (
    <div ref={wrapperRef} className="relative">
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((prev) => !prev)}
        className="flex h-12 w-full items-center justify-between gap-2 rounded-xl border border-gray-200 bg-white/0 p-3 text-left text-sm outline-none disabled:opacity-50 dark:!border-white/10 dark:text-white"
      >
        <span
          className={`truncate ${
            selected.length ? '' : 'text-gray-400 dark:text-white/50'
          }`}
        >
          {label}
        </span>
        <MdExpandMore
          className={`h-5 w-5 shrink-0 text-gray-400 transition-transform ${
            open ? 'rotate-180' : ''
          }`}
        />
      </button>

      {open && (
        <div className="absolute left-0 right-0 z-20 mt-1 max-h-60 overflow-y-auto rounded-xl border border-gray-200 bg-white p-1 shadow-lg dark:border-white/10 dark:bg-navy-800">
          {options.length === 0 ? (
            <p className="px-2 py-2 text-sm text-gray-400">No options</p>
          ) : (
            options.map((opt) => (
              <label
                key={opt.value}
                className="flex cursor-pointer items-center gap-2 rounded-lg px-2 py-2 text-sm text-navy-700 hover:bg-gray-50 dark:text-white dark:hover:bg-white/5"
              >
                <input
                  type="checkbox"
                  checked={selected.includes(opt.value)}
                  onChange={() => toggle(opt.value)}
                />
                <span className="truncate">{opt.label}</span>
              </label>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default MultiSelectDropdown;
